import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { BUTTON_LABELS, PERMISSIONS } from "@miplatform/constants";
import {
  useMiDaySettings,
  usePermissions,
  useSession,
} from "@mibuildweb/hooks";
import { Box, Grid } from "@mui/material";
import { ConfigPageLayout } from "../../Config/ConfigPageLayout";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { MiDaySettingToggleOptionCard } from "./MiDaySettingToggleOptionCard";

export function ManageMiDaySettings() {
  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.MI_DAY.ADMINISTRATION);
  const {
    session: { tenantId },
  } = useSession();

  const {
    isLoading,
    miDaySettings,
    getMiDaySettings,
    handleUpdateMiDaySetting,
    refresh,
    setRefresh,
  } = useMiDaySettings();

  const handleSave = () => {
    handleUpdateMiDaySetting({
      modifiedOn: miDaySettings?.modifiedOn,
      singleJobViewMode: selectedTechnicianViewMode === "single_job",
      automaticDispatch: selectedDispatchWorkflow === "automatic",
      automaticDispatchFutureJobs: selectedAutoDispatchMode === "future",
    });
  };

  const [selectedTechnicianViewMode, setSelectedTechnicianViewMode] = useState<
    string | null
  >(null);
  const [selectedDispatchWorkflow, setSelectedDispatchWorkflow] = useState<
    string | null
  >(null);
  const [selectedAutoDispatchMode, setSelectedAutoDispatchMode] = useState<
    string | null
  >(null);

  const isDirty =
    miDaySettings != null &&
    (selectedTechnicianViewMode !==
      (miDaySettings.singleJobViewMode ? "single_job" : "full_schedule") ||
      selectedDispatchWorkflow !==
        (miDaySettings.automaticDispatch ? "automatic" : "manual") ||
      selectedAutoDispatchMode !==
        (miDaySettings.automaticDispatchFutureJobs ? "future" : "today"));

  useEffect(() => {
    if (tenantId) {
      getMiDaySettings({ id: tenantId });
    }
  }, [refresh, tenantId]);

  useEffect(() => {
    if (!miDaySettings) {
      return;
    }

    setSelectedTechnicianViewMode(
      miDaySettings.singleJobViewMode ? "single_job" : "full_schedule"
    );
    setSelectedDispatchWorkflow(
      miDaySettings.automaticDispatch ? "automatic" : "manual"
    );
    setSelectedAutoDispatchMode(
      miDaySettings.automaticDispatchFutureJobs ? "future" : "today"
    );
  }, [miDaySettings]);

  const technicianViewModeOptionsData = [
    {
      label: "Single Job",
      value: "single_job",
      description: "Restricts technician to only view one job at a time",
    },
    {
      label: "Full Schedule",
      value: "full_schedule",
      description: "Allows technician to view their full schedule",
    },
  ];

  const dispatchWorkflowOptionsData = [
    {
      label: "Automatic",
      value: "automatic",
      description: "Automatically dispatches work",
    },
    {
      label: "Manual",
      value: "manual",
      description: "Requires manual dispatching of work",
    },
  ];

  const autoDispatchModeOptionsData = [
    {
      label: "Today",
      value: "today",
      description: "Only dispatches work for today",
    },
    {
      label: "Future",
      value: "future",
      description: "Dispatches work for future dates",
    },
  ];

  const canSaveSettings = () => {
    if (isLoading) return false;
    if (isReadOnly) return false;
    if (!isDirty) {
      return false;
    }
    return true;
  };

  const userActions = isReadOnly
    ? null
    : {
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => {
            setRefresh((prev) => !prev);
          },
        },
        submit: {
          label: "Save Changes",
          onClick: handleSave,
          disabled: !canSaveSettings(),
        },
      };

  const { classes } = useStyles();

  return (
    <ConfigPageLayout
      title="MiDay Settings"
      hideOnBack
      loading={isLoading}
      bottomActions={userActions}
    >
      <Box
        sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
        className={classes.verticalTabsLayoutViewContainer}
      >
        <ConfigAccordionHeader title="Technician View Mode">
          <Grid container spacing={2}>
            {technicianViewModeOptionsData.map((type) => (
              <Grid key={type.value} size={{ xs: 12, sm: 6 }}>
                <MiDaySettingToggleOptionCard
                  isDisabled={false}
                  {...type}
                  isSelected={type.value === selectedTechnicianViewMode}
                  onClick={() => {
                    setSelectedTechnicianViewMode(type.value);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Dispatch Workflow">
          <Grid container spacing={2}>
            {dispatchWorkflowOptionsData.map((type) => (
              <Grid key={type.value} size={{ xs: 12, sm: 6 }}>
                <MiDaySettingToggleOptionCard
                  isDisabled={false}
                  {...type}
                  isSelected={type.value === selectedDispatchWorkflow}
                  onClick={() => {
                    if (type.value !== "automatic") {
                      setSelectedAutoDispatchMode(null);
                    }
                    setSelectedDispatchWorkflow(type.value);
                  }}
                />
              </Grid>
            ))}
          </Grid>
          {selectedDispatchWorkflow === "automatic" && (
            <Grid
              container
              spacing={2}
              className={classes.autoDispatchModeContainer}
            >
              {autoDispatchModeOptionsData.map((type) => (
                <Grid key={type.value} size={{ xs: 12, sm: 6 }}>
                  <MiDaySettingToggleOptionCard
                    isDisabled={false}
                    {...type}
                    isSelected={type.value === selectedAutoDispatchMode}
                    onClick={() => {
                      setSelectedAutoDispatchMode(type.value);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </ConfigAccordionHeader>
      </Box>
    </ConfigPageLayout>
  );
}

const useStyles = makeStyles()((theme) => ({
  verticalTabsLayoutViewContainer: {
    overflow: "hidden",
    backgroundColor: theme.palette.grey.lightAccent,
    height: "100%",
  },
  autoDispatchModeContainer: {
    marginTop: theme.spacing(2),
  },
}));
